import React, {
    useCallback,
    useContext,
    useState
} from "react";
import axios from "axios";
import {
    useHistory
} from "react-router-dom";
import {
    Alert
} from "react-bootstrap";
import {
    Title
} from "@zandor300/backoffice-framework";

import ShopsContext from "../../context/ShopsContext";
import Helmet from "../../components/Helmet";
import DiscountCodeForm from "./components/DiscountCodeForm";
import DiscountCodeTypePicker from "./components/DiscountCodeTypePicker";

function NewDiscountCode() {
    const shopsContext = useContext(ShopsContext);
    const history = useHistory();

    const [discountCodeType, setDiscountCodeType] = useState("");
    const [formState, setFormState] = useState({
        code: "",
        discountValue: "",
        discountPercentage: "",
        enableDiscountValue: false,
        enableDiscountPercentage: false,
        enableMaxUsageCount: false,
        maxUsageCount: 1,
        enableStartDate: false,
        startDate: null,
        enableExpirationDate: false,
        expirationDate: null,
        combinable: true,
        enabled: true,
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const onChange = useCallback((newState) => {
        setFormState(prev => ({ ...prev, ...newState }));
    }, []);

    const validType = useCallback(() => {
        return ["coupon", "voucher", "promocode"].includes(discountCodeType);
    }, [discountCodeType]);

    // eslint-disable-next-line complexity
    const addDiscountCode = useCallback(() => {
        if(!validType()) {
            setError("Ongeldig type geselecteerd.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }
        if(formState.code.trim().length === 0) {
            setError("Code is vereist.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }

        // Discount validation: either value or percentage
        if(formState.enableDiscountValue && (!formState.discountValue || parseFloat(formState.discountValue) <= 0)) {
            setError("Vul een geldig kortingsbedrag in.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }
        if(!formState.enableDiscountValue && (!formState.discountPercentage || parseFloat(formState.discountPercentage) <= 0)) {
            setError("Vul een geldig kortingspercentage in.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }

        setError(null);
        setLoading(true);

        const discountValue = formState.enableDiscountValue ? parseFloat(formState.discountValue) : "null";
        const discountPercentage = !formState.enableDiscountValue ? parseFloat(formState.discountPercentage) : "null";

        axios.post("/addDiscountCode", {
            shopId: shopsContext.currentShop.id,
            code: formState.code.trim(),
            type: discountCodeType,
            discountValue,
            discountPercentage,
            maxUsageCount: formState.enableMaxUsageCount ? parseInt(formState.maxUsageCount, 10) : "null",
            startDate: formState.enableStartDate ? formState.startDate : "",
            expirationDate: formState.enableExpirationDate ? formState.expirationDate : "",
            allProductsEligible: 1,
            requiredProductsAll: "null",
            combinable: formState.combinable ? 1 : 0,
            enabled: formState.enabled ? 1 : 0,
        })
            .then((response) => {
                if(response.data.valid) {
                    const discountCodeId = response.data.discountCode.id;
                    history.push("/shop/" + shopsContext.currentShop.codeName + "/discount-code/" + discountCodeId);
                } else {
                    setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
                    setLoading(false);
                }
            })
            .catch((requestError) => {
                console.error(requestError);
                setError("Er ging iets fout. Probeer het later opnieuw.");
                setLoading(false);
            })
            .finally(() => {
                window.scroll({ top: 0, behavior: "smooth" });
            });
    }, [formState, discountCodeType, shopsContext.currentShop, history, validType]);

    const state = {
        ...formState,
        discountCodeType,
        type: discountCodeType,
    };

    return (
        <React.Fragment>
            <Helmet title="Kortingscode toevoegen"/>
            <Title preTitle="Overzicht">
                Kortingscode toevoegen
            </Title>
            { error && (
                <Alert variant="danger">{ error }</Alert>
            )}
            <div className="card mb-3">
                <div className="card-body">
                    <h4>Type kortingscode</h4>
                    <DiscountCodeTypePicker
                        discountCodeType={ discountCodeType }
                        setDiscountCodeType={ setDiscountCodeType }
                    />
                </div>
            </div>
            { validType() && (
                <DiscountCodeForm
                    state={ state }
                    onChange={ onChange }
                    loading={ loading }
                    onSave={ addDiscountCode }
                    formType="new"
                />
            )}
        </React.Fragment>
    )
}

export default React.memo(NewDiscountCode);
